import React from "react";
import { IDGSetReading } from "../../../vm";
import { ToastContext } from "../../common/ToastProvider";
import { ConfirmDialogContext } from "../../common/ConfirmDialogProvider";
import {
  IStackTokens,
  PanelType,
  Stack,
  Toggle,
  DefaultButton,
  PrimaryButton,
  DatePicker,
  DayOfWeek,
} from "office-ui-fabric-react";
import CustomDrawer from "../../common/CustomDrawer";
import * as yup from "yup";
import { updateDgSetReading } from "../../../services/infrastructure/DgSetService";
import { getAlertDetails } from "../../../services/ThresholdService";
import { Formik } from "formik";
import { CustomTextField } from "../../common/Library";
import { CategoryHeader } from "../Misc";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";

const singlePhaseVoltageSchema = yup.object().shape({
  RN: yup.string(),
  YN: yup.string(),
  BN: yup.string(),
});

const threePhaseVoltageSchema = yup.object().shape({
  RY: yup.string(),
  YB: yup.string(),
  BR: yup.string(),
});

const dgReadingSchema = yup.object().shape({
  oilLevel: yup.number().required("required"),
  waterLevelInCoolingTank: yup.number().required("required"),
  dieselLevelInTank: yup.number().required("required"),
  coolentLevel: yup.string().required("required"),
  batteryVoltage: yup.number().required("required"),
  engineOilPressure: yup.string(),
  oilTemperature: yup.string(),
  coolingWaterTemperature: yup.string(),
  batteryChargingVoltage: yup.string(),
  engineRPMTrip1650: yup.string(),
  hourMeterReading: yup
    .object()
    .shape({ afterStop: yup.string(), diff: yup.string() }),
  kwhMeterReading: yup
    .object()
    .shape({ afterStop: yup.string(), diff: yup.string() }),
  amfPanelKWHMeterReading: yup
    .object()
    .shape({ afterStop: yup.string(), diff: yup.string() }),
  dieselLevelDayTank: yup
    .object()
    .shape({ afterStop: yup.string(), diff: yup.string() }),
  // hourMeterReading: yup.string(),
  // kwhMeterReading: yup.string(),
  // amfPanelKWHMeterReading: yup.string(),
  // dieselLevelDayTank: yup.string(),
  averageUnit: yup.string(),
  singlePhaseVoltage: singlePhaseVoltageSchema,
  threePhaseVoltage: threePhaseVoltageSchema,
  loadInAmpsPerPhase: yup.string(),
  kva: yup.string(),
  kw: yup.string(),
  recordedBy: yup.string().required("required"),
  remarks: yup.string().optional(),
});

export interface ManageDGSetDialogProps {
  data?: IDGSetReading;
  handleLoading: any;
  handleDialogClose: any;
  threshold: any;
}

const ManageDGSetDialog: React.FC<ManageDGSetDialogProps> = ({
  data,
  handleDialogClose,
  handleLoading,
  threshold,
}) => {
  //state
  const [state, setState] = React.useState({
    dgReading: {
      ...data,
    },
    isLoading: false,
  } as {
    dgReading: IDGSetReading;
    isLoading: boolean;
  });

  //hooks
  const { showToast } = React.useContext(ToastContext);
  const { showConfirmDialog } = React.useContext(ConfirmDialogContext);

  const wrapStackTokens: IStackTokens = { childrenGap: 16 };

  const closeUpdateDialoge = (data?: IDGSetReading) => {
    handleDialogClose(data);
  };

  const onBlurFunc = (value: any, path: string) => {
    console.log("onBlurFunc");
    const { showAlert, msg } = getAlertDetails(path, threshold, value);
    console.log("[showAlert]", showAlert, msg);
    if (showAlert === true) {
      showConfirmDialog("Alert", msg as string, () => { }, "Ok", true);
    }
  };

  // React.useEffect(() => {
  //   console.log("dgReading", state.dgReading);
  //   if (!state.dgReading.recordedBy) {
  //     setState({ ...state, dgReading: { ...state.dgReading, recordedBy: "" } });
  //   }
  // }, []);

  const isPuneDc2 = ["5f0570c337b0eb553b960600"].includes(
    data?.datacenterId as string
  );
  const isLvsb = ["5db81204cf818f6abcffa08e"].includes(
    data?.datacenterId as string
  );
  const isCalDc1 = ["5db6dd36b27b36223779fd7c"].includes(
    data?.datacenterId as string
  );

  return (
    <React.Fragment>
      <CustomDrawer
        drawerType={PanelType.extraLarge}
        title={"Update DG Set Reading"}
        handelOpen={true}
        onClose={() => closeUpdateDialoge()}
      >
        <Stack>
          <Formik
            initialValues={state.dgReading}
            // validationSchema={dgReadingSchema}
            onSubmit={async (values: IDGSetReading, { setSubmitting }) => {
              let obj = { ...values };
              let result;
              handleLoading();
              result = await updateDgSetReading(obj);
              handleLoading();
              if (result?.success) {
                closeUpdateDialoge(result.data);
                showToast(`DG Set Reading updated successfully`, "success");
              } else {
                showToast(`Error while updating an dg set reading`, "error");
              }
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, setFieldValue, values }) => (
              <form onSubmit={handleSubmit}>
                <div className="ms-Grid">
                  <div className="ms-Grid-row">
                    {/* <div className="ms-Grid-col ms-xs12 ms-sm12 ms-md12 custom-toggle">
                      <Toggle
                        checked={values.isSystemOn}
                        onText="Yes"
                        offText="No"
                        onChange={(e, v) => setFieldValue("isSystemOn", v)}
                        label="System"
                      />
                    </div> */}
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"oilLevel"}
                        onBlurFunc={onBlurFunc}
                        // type="number"
                        label={"Oil Level"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"waterLevelInCoolingTank"}
                        onBlurFunc={onBlurFunc}
                        // type="number"
                        label={"Water Level In Coolant Tank"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"dieselLevelInTank"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Diesel Capacity In Tank (Litre)"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"coolentLevel"}
                        onBlurFunc={onBlurFunc}
                        // type="number"
                        label={"Coolent Level"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"batteryVoltage"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Battery Voltage"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"engineOilPressure"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={`Engine Oil Pressure ${isCalDc1 ? "(PSI)" : "(KPA)"
                          }`}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4 custom-toggle">
                      <Toggle
                        checked={values.isWaterLevelInBatteryBelowVent}
                        onText="Yes"
                        offText="No"
                        onChange={(e, v) =>
                          setFieldValue("isWaterLevelInBatteryBelowVent", v)
                        }
                        label="Water Level in Battery Below Vent"
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4 custom-toggle">
                      <Toggle
                        checked={values.isCoolingTowerPipeLineValvesOpen}
                        onText="Open"
                        offText="Close"
                        onChange={(e, v) =>
                          setFieldValue("isCoolingTowerPipeLineValvesOpen", v)
                        }
                        label="Cooling Tower Pipe Line Valves"
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4 custom-toggle">
                      <Toggle
                        checked={values.isLeakageInHoses}
                        onText="Leakages"
                        offText="No Leakages"
                        onChange={(e, v) =>
                          setFieldValue("isLeakageInHoses", v)
                        }
                        label="Leakage In Hoses"
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4 custom-toggle">
                      <Toggle
                        checked={values.anyAlarmInControlPanel}
                        onText="Alarms"
                        offText="No Alarms"
                        onChange={(e, v) =>
                          setFieldValue("anyAlarmInControlPanel", v)
                        }
                        label="Active Alarm In Control Panel (If Any)"
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4 custom-toggle">
                      <Toggle
                        checked={values.isDGSetClean}
                        onText="Clean"
                        offText="Not Clean"
                        onChange={(e, v) => setFieldValue("isDGSetClean", v)}
                        label="D.G Set for Cleaness"
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4 custom-toggle">
                      <Toggle
                        checked={values.exhaustAndCoolingWaterBellow}
                        onText="Yes"
                        offText="No"
                        onChange={(e, v) =>
                          setFieldValue("exhaustAndCoolingWaterBellow", v)
                        }
                        label="Exhaust & Cooling Water Bellow"
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4 custom-toggle">
                      <Toggle
                        checked={values.AnyDamageInMountingAVMPad}
                        onText="Damages"
                        offText="No Damages"
                        onChange={(e, v) =>
                          setFieldValue("AnyDamageInMountingAVMPad", v)
                        }
                        label="Inspect The Mounting (AVM Pads)"
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4 custom-toggle">
                      <Toggle
                        checked={values.exhaustFansFunction}
                        onText="On"
                        offText="Off"
                        onChange={(e, v) =>
                          setFieldValue("exhaustFansFunction", v)
                        }
                        label="Exhaust Fans Function"
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4 custom-datepicker">
                      {/* <DatePicker
                        firstDayOfWeek={DayOfWeek.Sunday}
                        showMonthPickerAsOverlay={true}
                        value={new Date(values.onTime)}
                        showGoToToday={false}
                        onSelectDate={(date: Date | null | undefined) =>
                          setFieldValue("onTime", date)
                        }
                        placeholder="Select a date..."
                        ariaLabel="Select a date"
                        label="Switched ON At"
                      /> */}
                      <label className="ms-Label">Switched ON At</label>
                      <Datetime
                        className="custom-field-group ms-TextField-fieldGroup"
                        inputProps={{
                          className: "ms-TextField-field px-8 border-0 w-100",
                        }}
                        dateFormat="ddd MMM DD YYYY"
                        timeFormat="HH:mm"
                        value={new Date(values.onTime)}
                        onChange={(date) => {
                          setFieldValue("onTime", date);
                        }}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4 custom-datepicker">
                      {/* <DatePicker
                        firstDayOfWeek={DayOfWeek.Sunday}
                        showMonthPickerAsOverlay={true}
                        value={new Date(values.offTime)}
                        showGoToToday={false}
                        onSelectDate={(date: Date | null | undefined) =>
                          setFieldValue("offTime", date)
                        }
                        placeholder="Select a date..."
                        ariaLabel="Select a date"
                        label="Switched OFF At"
                      /> */}

                      <label className="ms-Label">Switched OFF At</label>
                      <Datetime
                        className="custom-field-group ms-TextField-fieldGroup "
                        inputProps={{
                          className: "ms-TextField-field px-8 border-0 w-100",
                        }}
                        dateFormat="ddd MMM DD YYYY"
                        timeFormat="HH:mm"
                        value={new Date(values.offTime)}
                        onChange={(date) => {
                          setFieldValue("offTime", date);
                        }}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"oilTemperature"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Oil Temperature (℃)"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"coolingWaterTemperature"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={
                          !isPuneDc2
                            ? "Cooling Water Temp (℃)"
                            : "Coolant Temperature (℃)"
                        }
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"batteryChargingVoltage"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={
                          !isLvsb
                            ? "Battery Charging Voltage (Volt)"
                            : "Battery Current Parameter"
                        }
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"engineRPMTrip1650"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Engine R.P.M. Trip 1650"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"hourMeterReading.beforeStart"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Hour Meter Reading (Before Start)"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"hourMeterReading.afterStop"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Hour Meter Reading (After Stop)"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"hourMeterReading.diff"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Hour Meter Reading (Difference)"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"kwhMeterReading.beforeStart"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"K.W.H Meter Reading (Before Start)"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"kwhMeterReading.afterStop"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"K.W.H Meter Reading (After Stop)"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"kwhMeterReading.diff"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"K.W.H Meter Reading (Difference)"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"amfPanelKWHMeterReading.beforeStart"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"AMF Panel KWH Meter Reading(Before Start)"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"amfPanelKWHMeterReading.afterStop"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"AMF Panel KWH Meter Reading(After Stop)"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"amfPanelKWHMeterReading.diff"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"AMF Panel KWH Meter Reading(Difference)"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"dieselLevelDayTank.topUpToDayTank"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Diesel Level Day tank (Top Up To Day Tank)"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"dieselLevelDayTank.beforeStart"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Diesel Level in Day tank (Before Start)"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"dieselLevelDayTank.afterStop"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Diesel Level in Day tank (After Stop)"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"dieselLevelDayTank.diff"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Diesel Level in Day tank (Difference)"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"averageUnit"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Average Unit/Litre"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"loadInAmpsPerPhase"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"Load In AMPS Per Phase"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"kva"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"KVA"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"kw"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"KW"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"pf"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"PF"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm12">
                      <CategoryHeader title="Single Phase Voltage" />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"singlePhaseVoltage.RN"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"RN"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"singlePhaseVoltage.YN"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"YN"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"singlePhaseVoltage.BN"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"BN"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm12">
                      <CategoryHeader title="Three Phase Voltage" />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"threePhaseVoltage.RY"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"RY"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"threePhaseVoltage.YB"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"YB"}
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm6 ms-md4">
                      <CustomTextField
                        name={"threePhaseVoltage.BR"}
                        onBlurFunc={onBlurFunc}
                        type="number"
                        label={"BR"}
                      />
                    </div>

                    <div className="ms-Grid-col ms-xs12 ms-sm12">
                      <CategoryHeader title="General" />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm12">
                      <CustomTextField
                        name={"remarks"}
                        label={"Remarks (If Any)"}
                        multiline={true}
                        autoAdjustHeight
                      />
                    </div>
                    <div className="ms-Grid-col ms-xs12 ms-sm12">
                      <CustomTextField
                        name={"recordedBy"}
                        label={"Shift Tech Sign"}
                        disabled={true}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <Stack
                    horizontal
                    wrap
                    tokens={wrapStackTokens}
                    horizontalAlign="end"
                    className="pt-8"
                  >
                    <DefaultButton
                      text="Cancel"
                      onClick={() => closeUpdateDialoge()}
                      className="borderless-btn"
                    />
                    <PrimaryButton type="submit" text="Update" />
                  </Stack>
                </div>
                {/* <Stack wrap tokens={wrapStackTokens}>
                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"oilLevel"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Oil Level"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"waterLevelInCoolingTank"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Water Level In The Coolant Tank"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"dieselLevelInTank"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Diesel Level In The Tank"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"coolentLevel"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Coolent Level"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"batteryVoltage"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Battery Voltage"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"engineOilPressure"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Engine Oil Pressure"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <Toggle
                          checked={values.isWaterLevelInBatteryBelowVent}
                          onText="Yes"
                          offText="No"
                          onChange={(e, v) =>
                            setFieldValue("isWaterLevelInBatteryBelowVent", v)
                          }
                          label="Water Level in Battery Below Vent"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <Toggle
                          checked={values.isCoolingTowerPipeLineValvesOpen}
                          onText="Open"
                          offText="Close"
                          onChange={(e, v) =>
                            setFieldValue("isCoolingTowerPipeLineValvesOpen", v)
                          }
                          label="Cooling Tower Pipe Line Valves"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <Toggle
                          checked={values.isLeakageInHoses}
                          onText="Leakages"
                          offText="No Leakages"
                          onChange={(e, v) =>
                            setFieldValue("isLeakageInHoses", v)
                          }
                          label="Leakage In Hoses"
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <Toggle
                          checked={values.anyAlarmInControlPanel}
                          onText="Alarms"
                          offText="No Alarms"
                          onChange={(e, v) =>
                            setFieldValue("anyAlarmInControlPanel", v)
                          }
                          label="Active Alarm In Control Panel (If Any)"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <Toggle
                          checked={values.isDGSetClean}
                          onText="Clean"
                          offText="Not Clean"
                          onChange={(e, v) => setFieldValue("isDGSetClean", v)}
                          label="D.G Set for Cleaness"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <Toggle
                          checked={values.exhaustAndCoolingWaterBellow}
                          onText="Yes"
                          offText="No"
                          onChange={(e, v) =>
                            setFieldValue("exhaustAndCoolingWaterBellow", v)
                          }
                          label="Exhaust & Cooling Water Bellow"
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <Toggle
                          checked={values.AnyDamageInMountingAVMPad}
                          onText="Damages"
                          offText="No Damages"
                          onChange={(e, v) =>
                            setFieldValue("AnyDamageInMountingAVMPad", v)
                          }
                          label="Inspect The Mounting (AVM Pads)"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <Toggle
                          checked={values.exhaustFansFunction}
                          onText="On"
                          offText="Off"
                          onChange={(e, v) =>
                            setFieldValue("exhaustFansFunction", v)
                          }
                          label="Exhaust Fans Function"
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"oilTemperature"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Oil Temperature"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"coolingWaterTemperature"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Cooling Water Temp"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"batteryChargingVoltage"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Battery Charging Voltage"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"engineRPMTrip1650"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Engine R.P.M. Trip 1650"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"hourMeterReading.afterStop"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Hour Meter Reading (After Stop)"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"hourMeterReading.diff"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Hour Meter Reading (Difference)"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"kwhMeterReading.afterStop"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"K.W.H Meter Reading (After Stop"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"kwhMeterReading.diff"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"K.W.H Meter Reading (Difference)"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"amfPanelKWHMeterReading.afterStop"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"AMF Panel KWH Meter Reading (After Stop)"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"amfPanelKWHMeterReading.diff"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"AMF Panel KWH Meter Reading (Difference)"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"dieselLevelDayTank.afterStop"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Diesel Level in Day tank (After Stop)"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"dieselLevelDayTank.diff"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Diesel Level in Day tank (Difference)"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"averageUnit"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Average Unit/Litre"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="Single Phase Voltage" />
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"singlePhaseVoltage.RN"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"RN"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"singlePhaseVoltage.YN"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"YN"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"singlePhaseVoltage.BN"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"BN"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <CategoryHeader title="Three Phase Voltage" />
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"threePhaseVoltage.RY"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"RY"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"threePhaseVoltage.YB"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"YB"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"threePhaseVoltage.BR"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"BR"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack horizontal wrap tokens={wrapStackTokens}>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"loadInAmpsPerPhase"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"Load In AMPS Per Phase"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"kva"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"KVA"}
                        />
                      </Stack.Item>
                      <Stack.Item grow={1}>
                        <CustomTextField
                          name={"kw"}
                          onBlurFunc={onBlurFunc}
                          type="number"
                          label={"KW"}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <Stack.Item>
                    <CategoryHeader title="General" />
                    <Stack tokens={wrapStackTokens}>
                      <Stack.Item>
                        <CustomTextField
                          name={"remarks"}
                          label={"Remarks (If Any)"}
                          multiline={true}
                        />
                      </Stack.Item>
                      <Stack.Item>
                        <CustomTextField
                          name={"recordedBy"}
                          label={"Shift Tech Sign"}
                          disabled={true}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={wrapStackTokens}
                      horizontalAlign="end"
                    >
                      <DefaultButton
                        text="Cancel"
                        onClick={() => closeUpdateDialoge()}
                      />
                      <PrimaryButton type="submit" text="Update" />
                    </Stack>
                  </Stack.Item>
                </Stack> */}
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageDGSetDialog;
